/**
 * useAppointmentDragResize
 *
 * Restores the legacy `makeApptDraggable()` behavior from
 * `html/services/calendar-test.php` (L2729-L2942) on v5 scheduler cards:
 * vertical pointer-drag time-shift + bottom-edge resize, snapped to N-minute
 * increments. Drag is constrained to the same day/resource column the card
 * lives in — cross-day and cross-resource drag are out of scope for parity v1
 * (legacy only ever wired vertical drag within the active day cell).
 *
 * The hook converts pixel deltas to wall-clock minutes against the parent
 * column's bounding box, snaps, and invokes `onCommit` with naive
 * "YYYY-MM-DD HH:MM:SS" strings — the same format the AppointmentsController
 * accepts for `startTime`/`endTime` on PUT /appointments/{id}.
 *
 * A movement under `clickThresholdPx` is treated as a click; the caller's
 * existing click handler still fires (legacy `wasDragged` gate at
 * calendar-test.php L2727/L2839/L2893).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { minutesSinceMidnightInTz } from '@/lib/facilityTimezone';
import { computeVisibleMinutes } from '@/pages/scheduleShared';

export interface DragGhost {
  /** Top of the ghost rect inside the column, as a percentage 0-100. */
  topPct: number;
  /** Height of the ghost rect, as a percentage 0-100. */
  heightPct: number;
  /** Snapped wall-clock start label "h:mm AM" — shown inside the ghost. */
  startLabel: string;
  /** Snapped wall-clock end label "h:mm AM" — shown inside the ghost. */
  endLabel: string;
}

export interface UseAppointmentDragResizeOptions {
  /** Disables all wiring when false — used to gate on `aa_accounts.draggableAppts`. */
  enabled: boolean;
  /** UTC ISO start of the appointment as returned by /appointments/calendar. */
  currentStartIso: string;
  /** UTC ISO end of the appointment. */
  currentEndIso: string;
  /** "YYYY-MM-DD" wall-clock day the column represents (already in `userTz`). */
  dateStr: string;
  /** Active staff user's IANA timezone (resolved from `users_timezones`). */
  userTz: string;
  /** First visible hour of the grid column (0-24). */
  startHour: number;
  /** Last visible hour of the grid column (0-24). */
  hourEnd: number;
  /** The parent day-column element — needed so we can map pointer-y to minutes. */
  columnRef: React.RefObject<HTMLElement | null>;
  /** Legacy parity: 5-min snap (`calendar-test.php` L2757 / L2803). */
  snapMinutes?: number;
  /** Pixels-of-movement under which a pointer-up is treated as a click. */
  clickThresholdPx?: number;
  /**
   * Called after a real drag/resize with true UTC ISO `Z` strings (e.g.
   * "2026-05-27T17:30:00.000Z"). Computed as the original UTC instant
   * plus the snapped pixel-to-minute delta — TZ-agnostic, so the value
   * round-trips through `Carbon::parse` (which honours an explicit `Z`
   * suffix regardless of the `$sourceTz` arg) on both the dragCheck and
   * the PUT save endpoints. Earlier versions emitted naive
   * "YYYY-MM-DD HH:MM:SS" computed in `userTz`, which silently broke
   * for any user whose IANA TZ differs from the facility/account TZ.
   */
  onCommit: (newStart: string, newEnd: string) => void | Promise<void>;
}

export interface UseAppointmentDragResizeResult {
  /** True while a move-drag is in flight (use to set `cursor-grabbing`). */
  isDragging: boolean;
  /** True while a bottom-edge resize is in flight. */
  isResizing: boolean;
  /** Position + labels for the translucent preview rectangle, or null. */
  ghost: DragGhost | null;
  /** Spread onto the card body to enable click-and-drag time-shift. */
  cardHandlers: {
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  };
  /** Spread onto the bottom-edge resize handle (4px tall sliver). */
  resizeHandlers: {
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  };
  /**
   * True for ~250ms after a real drag/resize. Consumers should consult this in
   * their own onClick handler so a drag-up doesn't also open the detail modal
   * (mirror of legacy `wasDragged` guard at calendar-test.php L2727).
   */
  consumeClickGuard: () => boolean;
}

interface DragSession {
  mode: 'move' | 'resize';
  pointerId: number;
  /**
   * Element the pointerdown handler ran on. We hold this so we can defer
   * `setPointerCapture` until the click threshold has been crossed — see
   * the comment in `handlePointerMove` for why capturing eagerly breaks
   * the subsequent click target.
   */
  captureTarget: HTMLElement;
  startClientY: number;
  /** Snapped original start time (minutes since midnight, in userTz). */
  origStartMin: number;
  /** Snapped original end time (minutes since midnight, in userTz). */
  origEndMin: number;
  /** Cached column rect height (px) — captured at pointer-down for stable math. */
  columnHeightPx: number;
  /** Visible window length in minutes — captured at pointer-down. */
  visibleMinutes: number;
  /** Current snapped delta in minutes (sign matches drag direction). */
  deltaMin: number;
  /** Whether movement has crossed the click threshold yet. */
  exceededThreshold: boolean;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function applyMinutesToIso(originalIso: string, deltaMinutes: number): string {
  // Shift the original UTC instant by the snapped pointer delta. Output is
  // a true UTC ISO `Z` (no millisecond noise). Using the underlying
  // UTC instant is timezone-agnostic: the same minute delta represents the
  // same real-world shift regardless of the user's, facility's, or
  // account's IANA zones.
  const ms = new Date(originalIso).getTime() + deltaMinutes * 60000;
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
    + `T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}Z`;
}

function formatTimeLabel(totalMinutes: number): string {
  const hh24 = Math.floor(totalMinutes / 60);
  const mm = totalMinutes % 60;
  const ampm = hh24 >= 12 ? 'PM' : 'AM';
  const hh12 = ((hh24 + 11) % 12) + 1;
  return `${hh12}:${pad(mm)} ${ampm}`;
}

export function useAppointmentDragResize(
  options: UseAppointmentDragResizeOptions,
): UseAppointmentDragResizeResult {
  const {
    enabled,
    currentStartIso,
    currentEndIso,
    dateStr,
    userTz,
    startHour,
    hourEnd,
    columnRef,
    snapMinutes = 5,
    clickThresholdPx = 3,
    onCommit,
  } = options;

  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [ghost, setGhost] = useState<DragGhost | null>(null);

  const sessionRef = useRef<DragSession | null>(null);
  const clickGuardUntilRef = useRef<number>(0);
  const onCommitRef = useRef(onCommit);
  useEffect(() => {
    onCommitRef.current = onCommit;
  }, [onCommit]);

  // The commit reads the appointment's CURRENT times to apply its delta against,
  // but it runs inside the window-listener effect below, whose deps deliberately
  // exclude these ISOs — re-registering the pointer listeners every time the
  // times change would tear down an in-flight drag. Mirror the onCommit pattern
  // and read them through refs instead, so a second consecutive drag applies its
  // delta to the appointment's new position rather than the pre-drag one.
  const currentStartIsoRef = useRef(currentStartIso);
  const currentEndIsoRef = useRef(currentEndIso);
  useEffect(() => {
    currentStartIsoRef.current = currentStartIso;
    currentEndIsoRef.current = currentEndIso;
  }, [currentStartIso, currentEndIso]);

  const startSession = useCallback(
    (mode: 'move' | 'resize', event: React.PointerEvent<HTMLElement>) => {
      if (!enabled) return;
      // Ignore right-click / middle-click and modifier-augmented clicks so we
      // never hijack OS-level context menus or the ctrl-click "open in new
      // tab" affordance the existing card click flow may use later.
      if (event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey) return;

      const column = columnRef.current;
      if (!column) return;
      const rect = column.getBoundingClientRect();
      if (rect.height <= 0) return;

      // Shared with the event layout (and measured in the same `userTz`) so a
      // drag's pixels-per-minute matches the geometry the cards were drawn with,
      // including on DST transition days.
      const visibleMinutes = computeVisibleMinutes(startHour, hourEnd, dateStr, userTz);

      const startMinRaw = minutesSinceMidnightInTz(currentStartIso, userTz);
      const endMinRaw = minutesSinceMidnightInTz(currentEndIso, userTz);

      // Snap the originals so deltas accumulate against a slot-aligned base —
      // matches legacy which read the snapped "nearest" cell at drag start.
      const origStartMin = Math.round(startMinRaw / snapMinutes) * snapMinutes;
      const origEndMin = Math.max(
        origStartMin + snapMinutes,
        Math.round(endMinRaw / snapMinutes) * snapMinutes,
      );

      sessionRef.current = {
        mode,
        pointerId: event.pointerId,
        captureTarget: event.currentTarget as HTMLElement,
        startClientY: event.clientY,
        origStartMin,
        origEndMin,
        columnHeightPx: rect.height,
        visibleMinutes,
        deltaMin: 0,
        exceededThreshold: false,
      };

      // IMPORTANT: do NOT call setPointerCapture here. Capturing on
      // pointerdown re-targets the subsequent pointerup to the capture
      // element, which then makes the browser compute the synthesised
      // click event's target as that same wrapper element instead of the
      // child the user actually clicked. The inner ApptCard onClick that
      // opens the detail modal would never fire on simple click-without-
      // drag. We defer capture until handlePointerMove confirms a real
      // drag (after `clickThresholdPx`).
    },
    [
      enabled,
      columnRef,
      currentStartIso,
      currentEndIso,
      dateStr,
      hourEnd,
      snapMinutes,
      startHour,
      userTz,
    ],
  );

  useEffect(() => {
    if (!enabled) return;

    function buildGhost(session: DragSession): DragGhost {
      const visibleStart = startHour * 60;
      let newStart = session.origStartMin;
      let newEnd = session.origEndMin;
      if (session.mode === 'move') {
        newStart = session.origStartMin + session.deltaMin;
        newEnd = session.origEndMin + session.deltaMin;
      } else {
        newEnd = session.origEndMin + session.deltaMin;
        if (newEnd - newStart < snapMinutes) {
          newEnd = newStart + snapMinutes;
        }
      }
      const topPct = ((newStart - visibleStart) / session.visibleMinutes) * 100;
      const heightPct = ((newEnd - newStart) / session.visibleMinutes) * 100;
      return {
        topPct,
        heightPct,
        startLabel: formatTimeLabel(newStart),
        endLabel: formatTimeLabel(newEnd),
      };
    }

    function clampToColumn(session: DragSession, newStart: number, newEnd: number): {
      newStart: number;
      newEnd: number;
    } {
      const min = startHour * 60;
      const max = startHour * 60 + session.visibleMinutes;
      const duration = newEnd - newStart;
      if (session.mode === 'move') {
        if (newStart < min) {
          newStart = min;
          newEnd = newStart + duration;
        }
        if (newEnd > max) {
          newEnd = max;
          newStart = newEnd - duration;
        }
      } else {
        if (newEnd > max) newEnd = max;
        if (newEnd - newStart < snapMinutes) newEnd = newStart + snapMinutes;
      }
      return { newStart, newEnd };
    }

    function handlePointerMove(event: PointerEvent) {
      const session = sessionRef.current;
      if (!session || event.pointerId !== session.pointerId) return;

      const rawDeltaPx = event.clientY - session.startClientY;
      if (!session.exceededThreshold && Math.abs(rawDeltaPx) < clickThresholdPx) {
        return;
      }
      if (!session.exceededThreshold) {
        session.exceededThreshold = true;
        if (session.mode === 'move') setIsDragging(true);
        else setIsResizing(true);
        // Now that we KNOW it's a real drag, claim the pointer so the
        // user can leave the wrapper bounds without losing the drag — and
        // so we still consume the post-drop click reliably. The retarget
        // side-effect on click is fine here because we WANT to suppress
        // the click that would otherwise fire at drop time.
        try {
          session.captureTarget.setPointerCapture(session.pointerId);
        } catch {
          // setPointerCapture can throw if the element was detached
          // between events — safe to ignore, window-level pointermove /
          // pointerup listeners still keep the drag working.
        }
      }

      const minutesPerPx = session.visibleMinutes / session.columnHeightPx;
      const rawDeltaMin = rawDeltaPx * minutesPerPx;
      const snapped = Math.round(rawDeltaMin / snapMinutes) * snapMinutes;
      if (snapped === session.deltaMin) return;
      session.deltaMin = snapped;

      // Clamp so the ghost never escapes the visible window of the column.
      const provisional = buildGhost(session);
      let candidateStart = session.origStartMin
        + (session.mode === 'move' ? session.deltaMin : 0);
      let candidateEnd = session.origEndMin + session.deltaMin;
      const clamped = clampToColumn(session, candidateStart, candidateEnd);
      candidateStart = clamped.newStart;
      candidateEnd = clamped.newEnd;
      // Re-derive the session delta from the clamped result so subsequent
      // moves can't push past the boundary repeatedly.
      session.deltaMin = session.mode === 'move'
        ? candidateStart - session.origStartMin
        : candidateEnd - session.origEndMin;

      const visibleStart = startHour * 60;
      const ghostNext: DragGhost = {
        topPct: ((candidateStart - visibleStart) / session.visibleMinutes) * 100,
        heightPct: ((candidateEnd - candidateStart) / session.visibleMinutes) * 100,
        startLabel: formatTimeLabel(candidateStart),
        endLabel: formatTimeLabel(candidateEnd),
      };
      // Touch provisional once to keep the function pure-ish (unused result
      // intentional — the clamp branch above is the source of truth).
      void provisional;
      setGhost(ghostNext);
    }

    function handlePointerUp(event: PointerEvent) {
      const session = sessionRef.current;
      if (!session || event.pointerId !== session.pointerId) return;
      sessionRef.current = null;

      const wasRealDrag = session.exceededThreshold && session.deltaMin !== 0;

      setIsDragging(false);
      setIsResizing(false);
      setGhost(null);

      if (!wasRealDrag) return;

      // Set a short-lived click guard so the card's own onClick handler can
      // bail when the user releases the drag (mirror of legacy `wasDragged`).
      clickGuardUntilRef.current = Date.now() + 250;

      // Translate the pointer delta into independent start/end shifts. Move
      // shifts both ends by the same amount; resize only moves the end and
      // enforces a minimum duration of `snapMinutes`.
      let startDeltaMin = 0;
      let endDeltaMin = 0;
      if (session.mode === 'move') {
        startDeltaMin = session.deltaMin;
        endDeltaMin = session.deltaMin;
      } else {
        endDeltaMin = session.deltaMin;
        const projectedDuration = (session.origEndMin + endDeltaMin) - session.origStartMin;
        if (projectedDuration < snapMinutes) {
          endDeltaMin = (session.origStartMin + snapMinutes) - session.origEndMin;
        }
      }

      const newStartIso = applyMinutesToIso(currentStartIsoRef.current, startDeltaMin);
      const newEndIso = applyMinutesToIso(currentEndIsoRef.current, endDeltaMin);
      void onCommitRef.current(newStartIso, newEndIso);
    }

    function handlePointerCancel(event: PointerEvent) {
      const session = sessionRef.current;
      if (!session || event.pointerId !== session.pointerId) return;
      sessionRef.current = null;
      setIsDragging(false);
      setIsResizing(false);
      setGhost(null);
    }

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerCancel);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerCancel);
    };
  }, [enabled, clickThresholdPx, dateStr, snapMinutes, startHour]);

  const cardHandlers = {
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => startSession('move', event),
  };
  const resizeHandlers = {
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
      event.stopPropagation();
      startSession('resize', event);
    },
  };

  const consumeClickGuard = useCallback(() => {
    if (Date.now() < clickGuardUntilRef.current) {
      clickGuardUntilRef.current = 0;
      return true;
    }
    return false;
  }, []);

  return {
    isDragging,
    isResizing,
    ghost,
    cardHandlers,
    resizeHandlers,
    consumeClickGuard,
  };
}
